import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Download, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  Loader2,
  Archive
} from 'lucide-react';
import { apiService } from '../services/api';
import { FileMetadata } from '../../../shared/types';
import { handleApiError } from '../utils/api';

interface FileDownloadProps {
  selectedFiles: FileMetadata[];
  selectedFolders: any[]; // Using any[] since FolderStructure is not used in this component
  onDownloadStart?: () => void;
  onDownloadComplete?: () => void;
  onDownloadError?: (error: string) => void;
}

interface DownloadProgress {
  fileId: string;
  fileName: string;
  status: 'pending' | 'downloading' | 'completed' | 'error' | 'restore-required';
  error?: string;
  requiresRestore?: boolean;
  restoreStatus?: 'in-progress' | 'available';
}

export const FileDownload: React.FC<FileDownloadProps> = ({
  selectedFiles,
  onDownloadStart,
  onDownloadComplete,
  onDownloadError
}) => {
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Single file download mutation
  const singleDownloadMutation = useMutation({
    mutationFn: async (fileId: string) => {
      return await apiService.generateDownloadUrl({ fileId });
    },
  });

  // Batch download mutation
  const batchDownloadMutation = useMutation({
    mutationFn: async (fileIds: string[]) => {
      return await apiService.generateBatchDownloadUrl({ fileIds });
    },
  });

  const initiateDownload = (url: string, fileName: string) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSingleFileDownload = async (file: FileMetadata) => {
    setIsDownloading(true);
    onDownloadStart?.();

    const progress: DownloadProgress = {
      fileId: file.fileId,
      fileName: file.fileName,
      status: 'pending'
    };

    setDownloadProgress([progress]);

    try {
      // Update status to downloading
      setDownloadProgress([{ ...progress, status: 'downloading' }]);

      const response = await singleDownloadMutation.mutateAsync(file.fileId);

      if (response.requiresRestore) {
        // File is in Glacier and needs restoration
        setDownloadProgress([{
          ...progress,
          status: 'restore-required',
          requiresRestore: true,
          restoreStatus: response.restoreStatus
        }]);
        
        onDownloadError?.(`File "${file.fileName}" is archived and requires restoration. This may take 3-5 hours.`);
      } else {
        // File is available for immediate download
        initiateDownload(response.downloadUrl, response.fileName);
        
        setDownloadProgress([{ ...progress, status: 'completed' }]);
        onDownloadComplete?.();
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      setDownloadProgress([{ ...progress, status: 'error', error: errorMessage }]);
      onDownloadError?.(errorMessage);
    } finally {
      setIsDownloading(false);
      // Clear progress after 3 seconds
      setTimeout(() => setDownloadProgress([]), 3000);
    }
  };

  const handleBatchDownload = async (files: FileMetadata[]) => {
    setIsDownloading(true);
    onDownloadStart?.();

    const initialProgress: DownloadProgress[] = files.map(file => ({
      fileId: file.fileId,
      fileName: file.fileName,
      status: 'pending'
    }));

    setDownloadProgress(initialProgress);

    try {
      const fileIds = files.map(f => f.fileId);
      const response = await batchDownloadMutation.mutateAsync(fileIds);

      // Process each file's download URL
      const updatedProgress: DownloadProgress[] = [];
      let hasRestoreRequired = false;
      let downloadCount = 0;

      for (const urlData of response.urls) {
        const file = files.find(f => f.fileId === urlData.fileId);
        if (!file) continue;

        const progress: DownloadProgress = {
          fileId: urlData.fileId,
          fileName: urlData.fileName,
          status: 'downloading'
        };

        // Check if this file requires restoration (this would be indicated in the API response)
        // For now, we'll assume all files in the batch response are available
        try {
          initiateDownload(urlData.downloadUrl, urlData.fileName);
          progress.status = 'completed';
          downloadCount++;
        } catch (error) {
          progress.status = 'error';
          progress.error = 'Download failed';
        }

        updatedProgress.push(progress);
      }

      setDownloadProgress(updatedProgress);

      if (downloadCount > 0) {
        onDownloadComplete?.();
      }

      if (hasRestoreRequired) {
        onDownloadError?.('Some archived files require restoration and may take 3-5 hours to become available.');
      }

    } catch (error) {
      const errorMessage = handleApiError(error);
      const errorProgress = initialProgress.map(p => ({
        ...p,
        status: 'error' as const,
        error: errorMessage
      }));
      setDownloadProgress(errorProgress);
      onDownloadError?.(errorMessage);
    } finally {
      setIsDownloading(false);
      // Clear progress after 5 seconds for batch downloads
      setTimeout(() => setDownloadProgress([]), 5000);
    }
  };

  const handleDownload = async () => {
    if (selectedFiles.length === 0) return;

    if (selectedFiles.length === 1) {
      await handleSingleFileDownload(selectedFiles[0]);
    } else {
      await handleBatchDownload(selectedFiles);
    }
  };

  const canDownload = selectedFiles.length > 0;
  const downloadText = selectedFiles.length === 1 
    ? 'Download File' 
    : `Download ${selectedFiles.length} Files`;

  return (
    <div className="space-y-4">
      {/* Download Button */}
      <button
        onClick={handleDownload}
        disabled={!canDownload || isDownloading}
        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          canDownload && !isDownloading
            ? 'border-blue-300 text-blue-700 bg-white hover:bg-blue-50 focus:ring-blue-500'
            : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
        }`}
      >
        {isDownloading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Download className="w-4 h-4 mr-2" />
        )}
        {isDownloading ? 'Preparing...' : downloadText}
      </button>

      {/* Download Progress */}
      {downloadProgress.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Download Status</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {downloadProgress.map((progress) => (
              <div
                key={progress.fileId}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {progress.status === 'pending' && (
                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  {progress.status === 'downloading' && (
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                  )}
                  {progress.status === 'completed' && (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                  {progress.status === 'error' && (
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  {progress.status === 'restore-required' && (
                    <Archive className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                  
                  <span className="text-sm text-gray-900 truncate" title={progress.fileName}>
                    {progress.fileName}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  {progress.status === 'pending' && (
                    <span className="text-xs text-gray-500">Waiting...</span>
                  )}
                  {progress.status === 'downloading' && (
                    <span className="text-xs text-blue-600">Preparing...</span>
                  )}
                  {progress.status === 'completed' && (
                    <span className="text-xs text-green-600">Downloaded</span>
                  )}
                  {progress.status === 'error' && (
                    <span className="text-xs text-red-600" title={progress.error}>
                      Failed
                    </span>
                  )}
                  {progress.status === 'restore-required' && (
                    <div className="text-xs text-amber-600">
                      {progress.restoreStatus === 'in-progress' ? 'Restoring...' : 'Restore Required'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary for batch downloads */}
          {downloadProgress.length > 1 && (
            <div className="pt-2 border-t border-gray-200">
              <div className="flex justify-between text-xs text-gray-600">
                <span>
                  {downloadProgress.filter(p => p.status === 'completed').length} of {downloadProgress.length} completed
                </span>
                {downloadProgress.some(p => p.status === 'restore-required') && (
                  <span className="text-amber-600">Some files require restoration</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Glacier Restore Information */}
      {downloadProgress.some(p => p.status === 'restore-required') && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex">
            <Archive className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Archive Restoration Required
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  Some files are stored in Amazon Glacier and need to be restored before downloading. 
                  This process typically takes 3-5 hours for Flexible Retrieval.
                </p>
                <p className="mt-1">
                  You'll be able to download these files once the restoration is complete.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Info */}
      {selectedFiles.length > 0 && (
        <div className="text-xs text-gray-500">
          {selectedFiles.length === 1 ? (
            <span>1 file selected for download</span>
          ) : (
            <span>{selectedFiles.length} files selected for batch download</span>
          )}
          {selectedFiles.some(f => f.storageTier === 'archive') && (
            <span className="ml-2 text-amber-600">
              • Some files are in Archive Tier
            </span>
          )}
        </div>
      )}
    </div>
  );
};